import { createClient } from '@libsql/client';
import { nanoid } from 'nanoid';

const db = createClient({
  url: import.meta.env.VITE_TURSO_DB_URL,
  authToken: import.meta.env.VITE_TURSO_AUTH_TOKEN
});

const defaultContactContent = {
  id: 'contact_page_content',
  main_heading: 'Get In Touch With Us',
  main_subheading: 'We would love to hear from you! Please fill out the form below or reach us through the contact details provided.',
  contact_info: {
    phone: '+1 (123) 456-7890',
    email: 'info@antacres.com',
    address: '123 Real Estate Ave, Suite 100, Cityville, State, 90210',
    address_url: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3153.298285098906!2d144.9627777153169!3d-37.8172!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6ad642af0f11c759%3A0x5045675218d6a0!2sMelbourne%20VIC%2C%20Australia!5e0!3m2!1sen!2sus!4v1678888888888!5m2!1sen!2sus'
  },
  map_embed_url: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3153.298285098906!2d144.9627777153169!3d-37.8172!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6ad642af0f11c759%3A0x5045675218d6a0!2sMelbourne%20VIC%2C%20Australia!5e0!3m2!1sen!2sus!4v1678888888888!5m2!1sen!2sus',
  cta_heading: 'Ready to find your perfect property?',
  cta_subheading: 'Contact Antacres today and let our expert team guide you every step of the way.',
  cta_button_text: 'Get in Touch Today',
  cta_button_link: '/contact',
  updated_at: new Date().toISOString()
};

export const contactService = {
  async initializeDefaultContactContent() {
    try {
      const result = await db.execute({
        sql: 'SELECT id FROM contact_content WHERE id = ?',
        args: [defaultContactContent.id]
      });

      if (result.rows.length === 0) {
        const now = new Date().toISOString();
        await db.execute({
          sql: `INSERT INTO contact_content (
            id, main_heading, main_subheading, contact_info, map_embed_url, cta_heading, cta_subheading, cta_button_text, cta_button_link, updated_at
          ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          args: [
            defaultContactContent.id,
            defaultContactContent.main_heading,
            defaultContactContent.main_subheading,
            JSON.stringify(defaultContactContent.contact_info),
            defaultContactContent.map_embed_url,
            defaultContactContent.cta_heading,
            defaultContactContent.cta_subheading,
            defaultContactContent.cta_button_text,
            defaultContactContent.cta_button_link,
            now
          ],
        });
        console.log('Default contact content initialized.');
      }
    } catch (error) {
      console.error('Error initializing default contact content:', error);
      throw error;
    }
  },

  async getContactContent() {
    try {
      await this.setupContactTable();

      const result = await db.execute({
        sql: 'SELECT *, updated_at, cta_button_text, cta_button_link FROM contact_content WHERE id = ?',
        args: [defaultContactContent.id]
      });

      if (result.rows.length > 0) {
        const row = result.rows[0];
        return {
          id: row.id,
          main_heading: row.main_heading,
          main_subheading: row.main_subheading,
          contact_info: JSON.parse(row.contact_info),
          map_embed_url: row.map_embed_url,
          cta_heading: row.cta_heading,
          cta_subheading: row.cta_subheading,
          cta_button_text: row.cta_button_text,
          cta_button_link: row.cta_button_link,
          updated_at: row.updated_at
        };
      }
      return null;
    } catch (error) {
      console.error('Error fetching contact content:', error);
      throw error;
    }
  },

  async updateContactSection(sectionName, content) {
    const now = new Date().toISOString();
    try {
      let updateSql = '';
      let updateArgs = [];

      switch (sectionName) {
        case 'main':
          updateSql = `UPDATE contact_content SET main_heading = ?, main_subheading = ?, updated_at = ? WHERE id = ?`;
          updateArgs = [content.main_heading, content.main_subheading, now, defaultContactContent.id];
          break;
        case 'contact_info':
          updateSql = `UPDATE contact_content SET contact_info = ?, updated_at = ? WHERE id = ?`;
          updateArgs = [JSON.stringify(content), now, defaultContactContent.id];
          break;
        case 'map_embed':
          updateSql = `UPDATE contact_content SET map_embed_url = ?, updated_at = ? WHERE id = ?`;
          updateArgs = [content.map_embed_url, now, defaultContactContent.id];
          break;
        case 'cta':
          updateSql = `UPDATE contact_content SET cta_heading = ?, cta_subheading = ?, cta_button_text = ?, cta_button_link = ?, updated_at = ? WHERE id = ?`;
          updateArgs = [content.cta_heading, content.cta_subheading, content.cta_button_text, content.cta_button_link, now, defaultContactContent.id];
          break;
        default:
          throw new Error('Invalid contact section name');
      }

      await db.execute({
        sql: updateSql,
        args: updateArgs
      });

      return true;
    } catch (error) {
      console.error(`Error updating ${sectionName} contact section:`, error);
      throw error;
    }
  },

  async setupContactTable() {
    try {
      await db.execute(`
        CREATE TABLE IF NOT EXISTS contact_content (
          id TEXT PRIMARY KEY,
          main_heading TEXT,
          main_subheading TEXT,
          contact_info TEXT,
          map_embed_url TEXT,
          cta_heading TEXT,
          cta_subheading TEXT,
          cta_button_text TEXT,
          cta_button_link TEXT,
          updated_at TEXT
        );
      `);
      console.log('Contact content table checked/created.');
      await this.initializeDefaultContactContent();
    } catch (error) {
      console.error('Error setting up contact content table:', error);
      throw error;
    }
  }
};

contactService.setupContactTable();